import * as React from 'react'
import {
	createContext,
	type CSSProperties,
	type ReactNode,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react'
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import {
	defaultDropAnimationSideEffects,
	DndContext,
	type DragEndEvent,
	type DragOverEvent,
	DragOverlay,
	type DragStartEvent,
	type DropAnimation,
	KeyboardSensor,
	MeasuringStrategy,
	type Modifiers,
	MouseSensor,
	TouchSensor,
	type UniqueIdentifier,
	useSensor,
	useSensors,
	type DraggableAttributes,
	type DraggableSyntheticListeners,
} from '@dnd-kit/core'
import {
	arrayMove,
	defaultAnimateLayoutChanges,
	rectSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
	type AnimateLayoutChanges,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { createPortal } from 'react-dom'

import { cn } from '@/libs/tv'

interface KanbanContextProps<T> {
	columns: Record<string, T[]>
	setColumns: (columns: Record<string, T[]>) => void
	getItemId: (item: T) => string
	columnIds: string[]
	activeId: UniqueIdentifier | null
	setActiveId: (id: UniqueIdentifier | null) => void
	overId: UniqueIdentifier | null
	setOverId: (id: UniqueIdentifier | null) => void
	findContainer: (id: UniqueIdentifier) => string | undefined
	isColumn: (id: UniqueIdentifier) => boolean
	modifiers?: Modifiers
}

// oxlint-disable-next-line typescript/no-explicit-any
const KanbanContext = createContext<KanbanContextProps<any>>({
	columns: {},
	setColumns: () => {},
	getItemId: () => '',
	columnIds: [],
	activeId: null,
	setActiveId: () => {},
	overId: null,
	setOverId: () => {},
	findContainer: () => undefined,
	isColumn: () => false,
	modifiers: undefined,
})

const ColumnContext = createContext<{
	attributes: DraggableAttributes
	listeners: DraggableSyntheticListeners | undefined
	isDragging?: boolean
	disabled?: boolean
}>({
	attributes: {} as DraggableAttributes,
	listeners: undefined,
	isDragging: false,
	disabled: false,
})

const ItemContext = createContext<{
	listeners: DraggableSyntheticListeners | undefined
	isDragging?: boolean
	disabled?: boolean
}>({
	listeners: undefined,
	isDragging: false,
	disabled: false,
})

const IsOverlayContext = createContext(false)

const animateLayoutChanges: AnimateLayoutChanges = args => defaultAnimateLayoutChanges({ ...args, wasDragging: true })

const dropAnimationConfig: DropAnimation = {
	sideEffects: defaultDropAnimationSideEffects({
		styles: {
			active: {
				opacity: '0.4',
			},
		},
	}),
}

export interface KanbanMoveEvent {
	event: DragEndEvent
	activeContainer: string
	activeIndex: number
	overContainer: string
	overIndex: number
}

export interface KanbanRootProps<T> extends Omit<useRender.ComponentProps<'div'>, 'children'> {
	value: Record<string, T[]>
	onValueChange: (value: Record<string, T[]>) => void
	getItemValue: (item: T) => string
	children: ReactNode
	onMove?: (event: KanbanMoveEvent) => void
	modifiers?: Modifiers
}

function Kanban<T>({
	value,
	onValueChange,
	getItemValue,
	children,
	className,
	render,
	onMove,
	modifiers,
	...props
}: KanbanRootProps<T>) {
	const columns = value
	const setColumns = onValueChange
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
	const [overId, setOverId] = useState<UniqueIdentifier | null>(null)

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: 10,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 250,
				tolerance: 5,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const columnIds = useMemo(() => Object.keys(columns), [columns])

	const isColumn = useCallback((id: UniqueIdentifier) => columnIds.includes(id as string), [columnIds])

	const findContainer = useCallback(
		(id: UniqueIdentifier) => {
			if (isColumn(id)) return id as string
			return columnIds.find(key => columns[key]?.some(item => getItemValue(item) === id))
		},
		[columns, columnIds, getItemValue, isColumn],
	)

	const handleDragStart = useCallback((event: DragStartEvent) => {
		setActiveId(event.active.id)
	}, [])

	const handleDragOver = useCallback(
		(event: DragOverEvent) => {
			if (onMove) {
				return
			}

			const { active, over } = event
			if (!over) return

			if (isColumn(active.id)) return

			const activeContainer = findContainer(active.id)
			const overContainer = findContainer(over.id)

			if (!activeContainer || !overContainer) {
				return
			}

			if (activeContainer !== overContainer) {
				const activeItems = columns[activeContainer] ?? []
				const overItems = columns[overContainer] ?? []

				const activeIndex = activeItems.findIndex((item: T) => getItemValue(item) === active.id)
				let overIndex = overItems.findIndex((item: T) => getItemValue(item) === over.id)

				if (isColumn(over.id)) {
					overIndex = overItems.length
				}

				setOverId(over.id)

				const newActiveItems = [...activeItems]
				const newOverItems = [...overItems]
				const [movedItem] = newActiveItems.splice(activeIndex, 1) as [T]
				newOverItems.splice(overIndex, 0, movedItem)

				setColumns({
					...columns,
					[activeContainer]: newActiveItems,
					[overContainer]: newOverItems,
				})
			} else {
				const container = activeContainer
				const containerItems = columns[container] ?? []
				const activeIndex = containerItems.findIndex((item: T) => getItemValue(item) === active.id)
				const overIndex = containerItems.findIndex((item: T) => getItemValue(item) === over.id)

				setOverId(over.id)

				if (activeIndex !== overIndex) {
					setColumns({
						...columns,
						[container]: arrayMove(containerItems, activeIndex, overIndex),
					})
				}
			}
		},
		[findContainer, getItemValue, isColumn, setColumns, columns, onMove],
	)

	const handleDragCancel = useCallback(() => {
		setActiveId(null)
			setOverId(null)
		setOverId(null)
	}, [])

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			setActiveId(null)
			setOverId(null)

			if (!over) return

			if (onMove && !isColumn(active.id)) {
				const activeContainer = findContainer(active.id)
				const overContainer = findContainer(over.id)

				if (activeContainer && overContainer) {
					const activeItems = columns[activeContainer] ?? []
					const overItems = columns[overContainer] ?? []
					const activeIndex = activeItems.findIndex((item: T) => getItemValue(item) === active.id)
					const overIndex = isColumn(over.id)
						? overItems.length
						: overItems.findIndex((item: T) => getItemValue(item) === over.id)

					onMove({
						event,
						activeContainer,
						activeIndex,
						overContainer,
						overIndex,
					})
				}
				return
			}

			// Handle column reordering
			if (isColumn(active.id) && isColumn(over.id)) {
				const activeIndex = columnIds.indexOf(active.id as string)
				const overIndex = columnIds.indexOf(over.id as string)
				if (activeIndex !== overIndex) {
					const newOrder = arrayMove(Object.keys(columns), activeIndex, overIndex)
					const newColumns: Record<string, T[]> = {}

					for (const key of newOrder) {
						newColumns[key] = columns[key] || []
					}

					setColumns(newColumns)
				}
				return
			}

			const activeContainer = findContainer(active.id)
			const overContainer = findContainer(over.id)

			// Handle item reordering within the same column
			if (activeContainer && overContainer && activeContainer === overContainer && columns[activeContainer]) {
				const container = activeContainer
				const activeIndex = columns[container]!.findIndex((item: T) => getItemValue(item) === active.id)
				const overIndex = columns[container]!.findIndex((item: T) => getItemValue(item) === over.id)

				if (activeIndex !== overIndex) {
					setColumns({
						...columns,
						[container]: arrayMove(columns[container]!, activeIndex, overIndex),
					})
				}
			}
		},
		[columnIds, columns, findContainer, getItemValue, isColumn, setColumns, onMove],
	)

	const contextValue = useMemo(
		() => ({
			columns,
			setColumns,
			getItemId: getItemValue,
			columnIds,
			activeId,
			setActiveId,
			overId,
			setOverId,
			findContainer,
			isColumn,
			modifiers,
		}),
		[columns, setColumns, getItemValue, columnIds, activeId, overId, findContainer, isColumn, modifiers],
	)

	const defaultProps = {
		'data-slot': 'kanban',
		'data-dragging': activeId !== null,
		className: cn(activeId !== null ? 'cursor-grabbing!' : '', className),
		children,
	}

	return (
		<KanbanContext.Provider value={contextValue}>
			<DndContext
				sensors={sensors}
				modifiers={modifiers}
				measuring={{
					droppable: {
						strategy: MeasuringStrategy.Always,
					},
				}}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				{useRender({
					defaultTagName: 'div',
					render,
					props: mergeProps<'div'>(defaultProps, props),
				})}
			</DndContext>
		</KanbanContext.Provider>
	)
}

export type KanbanBoardProps = useRender.ComponentProps<'div'>

function KanbanBoard({ className, render, ...props }: KanbanBoardProps) {
	const { columnIds } = useContext(KanbanContext)

	const defaultProps = {
		'data-slot': 'kanban-board',
		className: cn(className),
		children: props.children,
	}

	return (
		<SortableContext items={columnIds} strategy={rectSortingStrategy}>
			{useRender({
				defaultTagName: 'div',
				render,
				props: mergeProps<'div'>(defaultProps, props),
			})}
		</SortableContext>
	)
}

export interface KanbanColumnProps extends useRender.ComponentProps<'div'> {
	value: string
	disabled?: boolean
}

function KanbanColumn({ value, className, render, disabled, ...props }: KanbanColumnProps) {
	const isOverlay = useContext(IsOverlayContext)

	const {
		setNodeRef,
		transform,
		transition,
		attributes,
		listeners,
		isDragging: isSortableDragging,
	} = useSortable({
		id: value,
		disabled: disabled || isOverlay,
		animateLayoutChanges,
	})

	if (isOverlay) {
		const defaultProps = {
			'data-slot': 'kanban-column',
			'data-value': value,
			'data-dragging': true,
			className: cn('group/kanban-column flex flex-col', className),
			children: props.children,
		}

		return (
			<ColumnContext.Provider
				value={{
					attributes: {} as DraggableAttributes,
					listeners: undefined,
					isDragging: true,
					disabled: false,
				}}
			>
				{useRender({
					defaultTagName: 'div',
					render,
					props: mergeProps<'div'>(defaultProps, props),
				})}
			</ColumnContext.Provider>
		)
	}

	const { activeId, overId, isColumn, findContainer } = useContext(KanbanContext)
	const isColumnDragging = activeId ? isColumn(activeId) : false
	const isOver = overId ? findContainer(overId) === value : false
	const isItemDragging = activeId ? !isColumn(activeId) : false

	const style = {
		transition,
		transform: CSS.Transform.toString(transform),
	} as CSSProperties

	const defaultProps = {
		'data-slot': 'kanban-column',
		'data-value': value,
		'data-dragging': isSortableDragging,
		'data-over': isOver && isItemDragging,
		'data-disabled': disabled,
		ref: setNodeRef,
		style,
		className: cn(
			'group/kanban-column flex flex-col',
			isSortableDragging ? 'opacity-50 z-50' : '',
			isOver && isItemDragging ? 'ring-1 ring-primary/40 bg-primary/5' : '',
			disabled ? 'opacity-50' : '',
			className,
		),
		children: props.children,
	}

	return (
		<ColumnContext.Provider value={{ attributes, listeners, isDragging: isColumnDragging, disabled }}>
			{useRender({
				defaultTagName: 'div',
				render,
				props: mergeProps<'div'>(defaultProps, props),
			})}
		</ColumnContext.Provider>
	)
}

export interface KanbanColumnHandleProps extends useRender.ComponentProps<'div'> {
	cursor?: boolean
}

function KanbanColumnHandle({ className, render, cursor = true, ...props }: KanbanColumnHandleProps) {
	const { attributes, listeners, isDragging, disabled } = useContext(ColumnContext)

	const defaultProps = {
		'data-slot': 'kanban-column-handle',
		'data-dragging': isDragging,
		'data-disabled': disabled,
		...attributes,
		...listeners,
		className: cn(
			'opacity-0 transition-opacity group-hover/kanban-column:opacity-100',
			cursor ? (isDragging ? 'cursor-grabbing!' : 'cursor-grab!') : '',
			className,
		),
		children: props.children,
	}

	return useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>(defaultProps, props),
	})
}

export interface KanbanItemProps extends useRender.ComponentProps<'div'> {
	value: string
	disabled?: boolean
	handle?: boolean
}

function KanbanItem({ value, className, render, disabled, handle, ...props }: KanbanItemProps) {
	const isOverlay = useContext(IsOverlayContext)

	const {
		setNodeRef,
		transform,
		transition,
		attributes,
		listeners,
		isDragging: isSortableDragging,
	} = useSortable({
		id: value,
		disabled: disabled || isOverlay,
		animateLayoutChanges,
	})

	if (isOverlay) {
		const defaultProps = {
			'data-slot': 'kanban-item',
			'data-value': value,
			'data-dragging': true,
			className: cn(className),
			children: props.children,
		}

		return (
			<ItemContext.Provider value={{ listeners: undefined, isDragging: true, disabled: false }}>
				{useRender({
					defaultTagName: 'div',
					render,
					props: mergeProps<'div'>(defaultProps, props),
				})}
			</ItemContext.Provider>
		)
	}

	const { activeId, isColumn } = useContext(KanbanContext)
	const isItemDragging = activeId ? !isColumn(activeId) : false

	const style = {
		transition,
		transform: CSS.Transform.toString(transform),
	} as CSSProperties

	const defaultProps = {
		'data-slot': 'kanban-item',
		'data-value': value,
		'data-dragging': isSortableDragging,
		'data-disabled': disabled,
		ref: setNodeRef,
		style,
		...attributes,
		...(handle ? {} : listeners),
		className: cn(isSortableDragging ? 'opacity-50 z-50' : '', disabled ? 'opacity-50' : '', className),
		children: props.children,
	}

	return (
		<ItemContext.Provider value={{ listeners, isDragging: isItemDragging, disabled }}>
			{useRender({
				defaultTagName: 'div',
				render,
				props: mergeProps<'div'>(defaultProps, props),
			})}
		</ItemContext.Provider>
	)
}

export interface KanbanItemHandleProps extends useRender.ComponentProps<'div'> {
	cursor?: boolean
}

function KanbanItemHandle({ className, render, cursor = true, ...props }: KanbanItemHandleProps) {
	const { listeners, isDragging, disabled } = useContext(ItemContext)

	const defaultProps = {
		'data-slot': 'kanban-item-handle',
		'data-dragging': isDragging,
		'data-disabled': disabled,
		...listeners,
		className: cn(cursor ? (isDragging ? 'cursor-grabbing!' : 'cursor-grab!') : '', className),
		children: props.children,
	}

	return useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>(defaultProps, props),
	})
}

export interface KanbanColumnContentProps extends useRender.ComponentProps<'div'> {
	value: string
}

function KanbanColumnContent({ value, className, render, ...props }: KanbanColumnContentProps) {
	const { columns, getItemId } = useContext(KanbanContext)

	const itemIds = useMemo(() => columns[value]?.map(getItemId) || [], [columns, getItemId, value])

	const defaultProps = {
		'data-slot': 'kanban-column-content',
		className: cn('flex flex-col gap-2', className),
		children: props.children,
	}

	return (
		<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
			{useRender({
				defaultTagName: 'div',
				render,
				props: mergeProps<'div'>(defaultProps, props),
			})}
		</SortableContext>
	)
}

export interface KanbanOverlayProps extends Omit<React.ComponentProps<typeof DragOverlay>, 'children'> {
	children?: ReactNode | ((params: { value: UniqueIdentifier; variant: 'column' | 'item' }) => ReactNode)
}

function KanbanOverlay({ children, className, ...props }: KanbanOverlayProps) {
	const { activeId, isColumn, modifiers } = useContext(KanbanContext)
	const [mounted, setMounted] = useState(false)

	useLayoutEffect(() => setMounted(true), [])

	const variant = activeId ? (isColumn(activeId) ? 'column' : 'item') : 'item'

	const content =
		activeId && children ? (typeof children === 'function' ? children({ value: activeId, variant }) : children) : null

	if (!mounted) return null

	return createPortal(
		<DragOverlay
			dropAnimation={dropAnimationConfig}
			modifiers={modifiers}
			className={cn('z-50', activeId ? 'cursor-grabbing' : '', className)}
			{...props}
		>
			<IsOverlayContext.Provider value={true}>{content}</IsOverlayContext.Provider>
		</DragOverlay>,
		document.body,
	)
}

export {
	Kanban,
	KanbanBoard,
	KanbanColumn,
	KanbanColumnHandle,
	KanbanItem,
	KanbanItemHandle,
	KanbanColumnContent,
	KanbanOverlay,
}
